import {useState, useEffect, useMemo} from 'react';
import {Link} from 'react-router-dom';
import Swal from 'sweetalert2';

import ListaResponsiva from 'components/Usuarios/Lista';
import http from 'services/http';
import { tipoOperador } from 'utils/tipoOperador';
import {useGetUserPermission} from "../../../../hook/permissions";
import PermissionGate from "../../../../utils/PermissionGate";
import axios from "axios";
import {alerta} from "../../../../components/Alert";

// Menu Cambistas (padrão) e menu Clientes usam esta mesma tela: muda o tipo pedido
// à API e os textos.
export default function Usuarios({ tipoUsuario }) {
    const operador = tipoOperador(tipoUsuario);

    const [listaUsuarios, setListaUsuarios] = useState([]);
    const [nivel, setNivel] = useState(localStorage.getItem('nivel'));
    const [gerentes, setGerentes] = useState([]);
    const [nomeFilter, setNomeFilter] = useState('');
    const [gerente, setGerente] = useState(0);
    const [filtroRisco, setFiltroRisco] = useState('todos');
    const [loadingAtivo, setLoadingAtivo] = useState(false);
    const [qtdVendedores, setQtdVendedores] = useState(0);
    const [permissoesGerente, setPermissoesGerente] = useState({criar_operador: true, adicionar_saldo: true});
    const [modalidades, setModalidades] = useState([]);

    // Função para extrair valores da cotação
    const extrairValoresCotacao = (nomeCotacao) => {
        if (!nomeCotacao) return { M: 0, C: 0, D: 0, G: 0 };
        
        const regex = /M:\s*(\d+)\s*\/\s*C:\s*(\d+)\s*\/\s*D:\s*(\d+)\s*\/\s*G:\s*(\d+)/;
        const match = nomeCotacao.match(regex);
        
        if (match) {
            return {
                M: parseInt(match[1]),
                C: parseInt(match[2]),
                D: parseInt(match[3]),
                G: parseInt(match[4])
            };
        }
        return { M: 0, C: 0, D: 0, G: 0 };
    };

    // Função para calcular indicador de Milhar
    const calcularIndicadorMilhar = (cotacao, comissao) => {
        const M = cotacao + (comissao * 100);
        if (M > 9999) return 'ALTA';
        if (M >= 8999 && M <= 9999) return 'MÉDIA';
        return 'BAIXA';
    };

    // Função para calcular indicador de Centena
    const calcularIndicadorCentena = (cotacao, comissao) => {
        const C = cotacao + (comissao * 10);
        if (C > 999) return 'ALTA';
        if (C >= 899 && C <= 999) return 'MÉDIA';
        return 'BAIXA';
    };

    // Função para calcular indicador de Dezena
    const calcularIndicadorDezena = (cotacao, comissao) => {
        const D = cotacao + comissao;
        if (D > 99) return 'ALTA';
        if (D >= 89 && D <= 99) return 'MÉDIA';
        return 'BAIXA';
    };

    // Função para calcular indicador de Grupo
    const calcularIndicadorGrupo = (cotacao, comissao) => {
        const G = 25.00 - cotacao - (comissao * 0.25);
        if (G <= 0) return 'ALTA';
        if (G > 0 && G < 2.50) return 'MÉDIA';
        return 'BAIXA';
    };

    // Função para obter risco geral do usuário
    const obterRiscoUsuario = (usuario, modalidadesLista) => {
        // Usa a lista de modalidades passada como parâmetro ou a do estado
        const listaModalidades = modalidadesLista || modalidades;
        const cotacao = listaModalidades[usuario.cotas - 1];
        
        if (!cotacao) return { nivel: 'SEM COTAÇÃO', cor: '#6c757d' };
        
        const valores = extrairValoresCotacao(cotacao.nome);
        const indMilhar = calcularIndicadorMilhar(valores.M, parseFloat(usuario.comissao_milhar) || 0);
        const indCentena = calcularIndicadorCentena(valores.C, parseFloat(usuario.comissao_centena) || 0);
        const indDezena = calcularIndicadorDezena(valores.D, parseFloat(usuario.comissao_dezena) || 0);
        const indGrupo = calcularIndicadorGrupo(valores.G, parseFloat(usuario.comissao_grupo) || 0);

        const indicadores = [indMilhar, indCentena, indDezena, indGrupo];

        // Se algum estiver ALTA, é RUIM
        if (indicadores.some(ind => ind === 'ALTA')) {
            return { nivel: 'RUIM', cor: '#dc3545' };
        }
        
        // Se algum estiver MÉDIA, é MÉDIO
        if (indicadores.some(ind => ind === 'MÉDIA')) {
            return { nivel: 'MEDIO', cor: '#ffc107' };
        }
        
        return { nivel: 'BOM', cor: '#28a745' };
    };

    const requestUsuarios = async () => {
        try {
            setLoadingAtivo(true);

            const response = await http.get('/usuarios', {
                params: {
                    nivel: 'operador',
                    tipo_usuario: operador.tipo
                }
            });

            const resModalidades = await http.get('bancas/titulo-cotacoes');

            const {data: modalidadesData} = resModalidades.data;
            setModalidades(modalidadesData);

            // Busca gerentes primeiro
            let gerentesData = [];
            if (nivel === 'admin') {
                const responseGerentes = await http.get('/usuarios', {
                    params: {
                        nivel: 'gerente'
                    }
                });
                gerentesData = Object.values(responseGerentes.data.data);
                setGerentes(gerentesData);
            }

            const usuarios = response.data.data.map((u) => {
                u.cotaNome = modalidadesData[u.cotas - 1].nome;
                
                // Adiciona informação do gerente
                if (gerentesData.length > 0) {
                    const gerenteEncontrado = gerentesData.find(g => g.id === u.gerente_id);
                    if (gerenteEncontrado) {
                        u.gerente = { login: gerenteEncontrado.login, id: gerenteEncontrado.id };
                    }
                }
                
                // Transforma os campos de comissão de string para número
                u.comissao_milhar = parseFloat(u.comissao_milhar) || parseFloat(u.comissao) || 0;
                u.comissao_centena = parseFloat(u.comissao_centena) || parseFloat(u.comissao) || 0;
                u.comissao_dezena = parseFloat(u.comissao_dezena) || 0;
                u.comissao_grupo = parseFloat(u.comissao_grupo) || 0;
                
                u.risco = obterRiscoUsuario(u, modalidadesData);
                return u;
            });

            setListaUsuarios(usuarios);
            setQtdVendedores(usuarios.length);

            setLoadingAtivo(false);
        } catch (e) {
            alerta(e.response.data)
        }
    };

    useEffect(() => {
        const permissoesGerente = JSON.parse(localStorage.getItem('usuario')).permissoes_gerente;

        if (permissoesGerente) {
            setPermissoesGerente(JSON.parse(localStorage.getItem('usuario')).permissoes_gerente)
        }
        requestUsuarios();
    }, []);

    // Recalcula risco quando modalidades mudam
    useEffect(() => {
        if (modalidades.length > 0 && listaUsuarios.length > 0) {
            const usuariosAtualizados = listaUsuarios.map((u) => ({
                ...u,
                risco: obterRiscoUsuario(u, modalidades)
            }));
            setListaUsuarios(usuariosAtualizados);
        }
    }, [modalidades]);

    const rangerButton2 = async (id, ativo) => {
        try {
            await http.patch('usuarios/' + id + '/status', {
                status: !ativo
            });

            const novaLista = listaUsuarios.map((item) => {
                if (item.id === id) {
                    item.ativo = !item.ativo;
                }

                return item;
            });

            setListaUsuarios(novaLista);
        } catch (e) {
        }
    };

    const perguntarParaApagar = async (id, ativo) => {
        Swal.fire({
            title: `Deseja apagar o ${operador.singular}?`,
            text: `Todas as apostas serão apagadas junto ao ${operador.singular}!`,
            type: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            reverseButtons: true,
            confirmButtonText: 'Sim, apagar!',
            cancelButtonText: 'Não, cancelar'
        }).then((result) => {
            if (result.value) {
                handleDelete(id, ativo);
            }
        });
    };

    const handleDelete = async (id) => {
        try {
            const res = await http.delete('/usuarios/' + id);
            alerta(res.data);

            requestUsuarios();
        } catch (e) {
            alerta(e.response.data);
        }
    };

    const usuariosFiltrados = useMemo(() => {
        const filtro = nomeFilter.toString().toLowerCase();
        const gerenteId = gerente.toString();

        let usuarios = listaUsuarios;

        if (gerenteId > 0) {
            usuarios = usuarios.filter((u) => u.gerente_id.toString() === gerenteId);
        }

        if (filtro) {
            usuarios = usuarios.filter((u) => u.login.toLowerCase().includes(filtro));
        }

        // Filtro de risco
        if (filtroRisco !== 'todos') {
            usuarios = usuarios.filter((u) => {
                if (!u.risco) return false;
                return u.risco.nivel.toLowerCase() === filtroRisco.toLowerCase();
            });
        }

        setQtdVendedores(usuarios.length);

        return usuarios;
    }, [nomeFilter, gerente, filtroRisco, listaUsuarios]);

    return (
        <>
            <div className="titulo-principal">{operador.titulo}</div>
            <div className="conteudo-principal d-flex tres-colunas">
                <PermissionGate permissions={["Listar gerente"]}>
                    <select
                        onChange={(e) => setGerente(e.target.value)}
                        className="form-control"
                    >
                        <option value="0">Todos</option>
                        {gerentes.map((g) => (
                            <option key={g.id} value={g.id}>
                                {g.login}
                            </option>
                        ))}
                    </select>
                </PermissionGate>
                <input
                    onChange={(e) => setNomeFilter(e.target.value)}
                    type="text"
                    className="form-control"
                    placeholder="Filtrar por nome"
                />
                <select
                    onChange={(e) => setFiltroRisco(e.target.value)}
                    className="form-control"
                    value={filtroRisco}
                    style={{
                        backgroundColor: filtroRisco === 'bom' ? '#d4edda' : 
                                       filtroRisco === 'medio' ? '#fff3cd' : 
                                       filtroRisco === 'ruim' ? '#f8d7da' : '#fff'
                    }}
                >
                    <option value="todos">Todos (Risco)</option>
                    <option value="bom" style={{ backgroundColor: '#d4edda' }}>Bom (Baixo)</option>
                    <option value="medio" style={{ backgroundColor: '#fff3cd' }}>Médio</option>
                    <option value="ruim" style={{ backgroundColor: '#f8d7da' }}>Ruim (Alto)</option>
                </select>
                {permissoesGerente?.criar_operador && (
                    <Link
                        style={{height: '38px'}}
                        className="btn btn-info"
                        to={operador.rotaCriar}>
                        {operador.botaoCriar}
                    </Link>
                )}
            </div>

            <div className="subtitulo">{`${qtdVendedores} ${operador.contagem}`}</div>

            {loadingAtivo ? (
                <div className="loader"/>
            ) : (
                <ListaResponsiva
                    tBody={usuariosFiltrados}
                    showButton1={true}
                    showButton2={true}
                    tButton3={perguntarParaApagar}
                    tButton2={rangerButton2}
                    nivel="operador"
                    permissionApagar={'Remover cambista'}
                />
            )}
        </>
    );
}